import { forwardRef, useCallback, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { Icon } from "@/components/ui/Icon/Icon";
import styles from "./Checkbox.module.css";

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Optional label rendered alongside the box. */
  label?: ReactNode;
  /** Tri-state: renders a dash. `indeterminate` is a DOM property, not an
   * attribute, so we set it on the node via a ref callback. */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, id, indeterminate = false, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = indeterminate;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [ref, indeterminate],
  );

  return (
    <label htmlFor={inputId} className={[styles.row, className].filter(Boolean).join(" ")}>
      <input ref={setRef} id={inputId} type="checkbox" className={styles.input} {...rest} />
      <span className={styles.box} aria-hidden="true">
        {indeterminate ? <span className={styles.dash} /> : <Icon name="check" size={11} strokeWidth={3} />}
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
});
